import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
// __EQUIP_COMPAT__ (2026-10-04) every equipment variable per type, in the viewer's units
import EquipmentSpecFields from './EquipmentSpecFields';
import { TRUCK_TYPES, typeInfo, unitCapacity, fmtFt, fmtLb, bodyCoverOf, typeForCover, coverCraneConflict, COVER_TYPES } from '../utils/equipmentCompat';
import { specToForm, formToSpec, applyTypeDefaults, missingSpecs } from '../utils/equipmentForm';
import { getDefaultUnitSystem } from '../utils/units';
import { invalidateEquipmentCache } from '../services/equipmentService';
import { UnitDowntimeEditor } from './equipment/UnitDowntime';   // __UNIT_DOWNTIME__
import { OperatingLimitsEditor } from './equipment/OperatingLimits';   // __OPERATING_LIMITS__
import { TelematicsEditor } from './equipment/Telematics';   // __TELEMATICS__

const EMPTY_TRUCK = {
  truck_number: '',
  license_plate: '',
  vin: '',
  make: '',
  model: '',
  year: '',
  color: '',
  truck_type: 'day_cab',
  axle_count: 2,
  fuel_type: 'diesel',
  status: 'available',
  is_active: true,
  notes: ''
};

function TruckManagementModal({ onClose, onSuccess }) {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTruck, setEditingTruck] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const units = getDefaultUnitSystem();
  // Form state: the record's own fields + its spec (weights / deck / features) in display units
  const [formData, setFormData] = useState({ ...EMPTY_TRUCK });
  const [spec, setSpec] = useState(() => specToForm(null, units));

  useEffect(() => {
    fetchTrucks();
  }, []);

  const fetchTrucks = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('trucks')
        .select('*')
        .order('truck_number', { ascending: true });

      if (error) throw error;
      setTrucks(data || []);
    } catch (err) {
      console.error('Error fetching trucks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_TRUCK });
    setSpec(specToForm(null, units));
    setEditingTruck(null);
    setError(null);
  };

  const handleEdit = (truck) => {
    setFormData({
      truck_number: truck.truck_number || '',
      license_plate: truck.license_plate || '',
      vin: truck.vin || '',
      make: truck.make || '',
      model: truck.model || '',
      year: truck.year || '',
      color: truck.color || '',
      truck_type: truck.truck_type || 'day_cab',
      axle_count: truck.axle_count || 2,
      fuel_type: truck.fuel_type || 'diesel',
      status: truck.status || 'available',
      is_active: truck.is_active !== false,
      notes: truck.notes || ''
    });
    setSpec(specToForm(truck, units));
    setEditingTruck(truck);
    setShowForm(true);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Validate required fields (the trucks table requires a unique plate (and a max weight -
      // saved as 0 = unknown when blank),
      // and fuel_type may only be 'diesel' or 'gas' - the old form sent 'Diesel' and every
      // new truck failed the database check)
      if (!formData.truck_number.trim()) {
        throw new Error('Truck number is required');
      }
      if (!formData.license_plate.trim()) {
        throw new Error('License plate is required');
      }
      const s = formToSpec(spec, 'truck', units);
      // __SITE_LOADING__ a rolling-tarp / curtain-side body can't have the crane (the crane truck is open)
      const coverWhy = coverCraneConflict(formData.truck_type, s.features);
      if (coverWhy) throw new Error(coverWhy);

      const truckData = {
        truck_number: formData.truck_number.trim(),
        license_plate: formData.license_plate.trim(),
        vin: formData.vin || null,
        make: formData.make || null,
        model: formData.model || null,
        color: formData.color || null,
        truck_type: formData.truck_type,
        fuel_type: formData.fuel_type === 'gas' ? 'gas' : 'diesel',
        status: formData.status,
        is_active: formData.is_active !== false,
        notes: formData.notes || null,
        year: formData.year ? parseInt(formData.year, 10) : null,
        axle_count: formData.axle_count ? parseInt(formData.axle_count, 10) : 2,
        ...s,
        // integer NOT NULL column: 0 = not known yet (the check treats 0 as missing and says so)
        max_weight_kg: Math.round(s.max_weight_kg || 0)
      };

      if (editingTruck) {
        // Update existing truck
        const { error } = await supabase
          .from('trucks')
          .update(truckData)
          .eq('id', editingTruck.id);

        if (error) throw error;
      } else {
        // Create new truck
        const { error } = await supabase
          .from('trucks')
          .insert([truckData]);

        if (error) throw error;
      }

      invalidateEquipmentCache();
      // Refresh truck list
      await fetchTrucks();
      resetForm();
      setShowForm(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error saving truck:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (truck) => {
    if (deleteConfirmId !== truck.id) { setDeleteConfirmId(truck.id); return; }
    setDeleteConfirmId(null);

    try {
      const { error } = await supabase
        .from('trucks')
        .delete()
        .eq('id', truck.id);

      if (error) throw error;
      await fetchTrucks();
    } catch (err) {
      console.error('Error deleting truck:', err);
      setError(err.message);
    }
  };

  const filteredTrucks = trucks.filter(t => 
    t.truck_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'var(--color-success)';
      case 'in_use': return 'var(--accent-blue)';
      case 'maintenance': return 'var(--color-warning)';
      case 'out_of_service': return 'var(--color-danger)';
      default: return 'var(--text-muted)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Available';
      case 'in_use': return 'In Use';
      case 'maintenance': return 'Maintenance';
      case 'out_of_service': return 'Out of Service';
      default: return status;
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: 'var(--surface-2)',
    border: '1px solid var(--border-default)',
    borderRadius: '5px',
    color: 'var(--text-primary)',
    fontSize: '1rem'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: 'var(--text-secondary)',
    fontSize: '0.9rem'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--surface-1)',
        borderRadius: '10px',
        padding: '2rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--surface-2)'
        }}>
          <h2 style={{ color: 'var(--accent-primary)', margin: 0 }}>
            {showForm ? (editingTruck ? 'Edit Truck' : 'Add Truck') : 'Manage Trucks'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            x
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: 'var(--color-danger)',
            color: 'var(--text-primary)',
            padding: '1rem',
            borderRadius: '5px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {showForm ? (
          /* Truck Form */
          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              {/* Truck Number */}
              <div>
                <label style={labelStyle}>Truck Number *</label>
                <input
                  type="text"
                  value={formData.truck_number}
                  onChange={(e) => setFormData({ ...formData, truck_number: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., T-001"
                  required
                />
              </div>

              {/* License Plate */}
              <div>
                <label style={labelStyle}>License Plate *</label>
                <input
                  type="text"
                  value={formData.license_plate}
                  onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., ABC 1234"
                  required
                />
              </div>

              {/* VIN */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>VIN</label>
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                  style={inputStyle}
                  placeholder="Vehicle Identification Number"
                />
              </div>

              {/* Make */}
              <div>
                <label style={labelStyle}>Make</label>
                <input
                  type="text"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., Freightliner"
                />
              </div>

              {/* Model */}
              <div>
                <label style={labelStyle}>Model</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., Cascadia"
                />
              </div>

              {/* Year */}
              <div>
                <label style={labelStyle}>Year</label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., 2022"
                  min="1990"
                  max="2030"
                />
              </div>

              {/* Color */}
              <div>
                <label style={labelStyle}>Color</label>
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., White"
                />
              </div>

              {/* Truck Type */}
              <div>
                <label style={labelStyle}>Truck Type</label>
                <select
                  value={formData.truck_type}
                  onChange={(e) => {
                    setFormData({ ...formData, truck_type: e.target.value });
                    if (!editingTruck) setSpec(applyTypeDefaults(spec, 'truck', e.target.value, units));
                  }}
                  style={inputStyle}
                >
                  {Object.entries(TRUCK_TYPES).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
                  {formData.truck_type && !TRUCK_TYPES[formData.truck_type] && <option value={formData.truck_type}>{formData.truck_type}</option>}
                </select>
              </div>

              {/* __SITE_LOADING__ (2026-10-17) Body / cover: open / rolling tarp (loads every way) / curtain side (hard roof:
                  dock or side only, no overhead crane). It sets the truck type; the crane truck is an open truck. */}
              {bodyCoverOf('truck', formData.truck_type) && (
                <div>
                  <label style={labelStyle}>Body / cover</label>
                  <select value={bodyCoverOf('truck', formData.truck_type)} style={inputStyle}
                    onChange={(e) => setFormData({ ...formData, truck_type: typeForCover(formData.truck_type, e.target.value) })}>
                    {Object.entries(COVER_TYPES).map(([k, l]) => (
                      <option key={k} value={k} disabled={k !== 'open' && ((spec.features || []).includes('crane') || (spec.features || []).includes('self_unload') || formData.truck_type === 'crane_truck')}>
                        {l}{k === 'roll_tarp' ? ' (loads every way)' : k === 'curtain_side' ? ' (hard roof: dock / side, no overhead crane)' : ''}
                      </option>
                    ))}
                  </select>
                  {coverCraneConflict(formData.truck_type, spec.features) && (
                    <small style={{ color: 'var(--color-danger)', fontSize: '0.72rem' }}>{coverCraneConflict(formData.truck_type, spec.features)}</small>
                  )}
                  {(formData.truck_type === 'crane_truck') && <small style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>The crane truck is open: no tarp or curtain.</small>}
                </div>
              )}

              {/* Axle Count */}
              <div>
                <label style={labelStyle}>Axle Count</label>
                <input
                  type="number"
                  value={formData.axle_count}
                  onChange={(e) => setFormData({ ...formData, axle_count: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., 2"
                  min="2"
                  max="10"
                />
              </div>

              {/* __EQUIP_COMPAT__ weights / deck / features for this type */}
              <EquipmentSpecFields kind="truck" type={formData.truck_type} spec={spec} setSpec={setSpec} units={units}
                inputStyle={inputStyle} labelStyle={labelStyle} />

              {/* Fuel Type */}
              <div>
                <label style={labelStyle}>Fuel Type</label>
                <select
                  value={formData.fuel_type}
                  onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                  style={inputStyle}
                >
                  <option value="diesel">Diesel</option>
                  <option value="gas">Gas</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label style={labelStyle}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={inputStyle}
                >
                  <option value="available">Available</option>
                  <option value="in_use">In Use</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="out_of_service">Out of Service</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.4rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={formData.is_active !== false} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                  Active (untick to retire it - kept for history)
                </label>
                <small style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>Maintenance / Out of Service / retired: assigning a job to it shows the equipment warning.</small>
              </div>

              {/* __UNIT_DOWNTIME__ dated maintenance / out-of-service days (an existing truck) */}
              {editingTruck && <UnitDowntimeEditor kind="truck" unitId={editingTruck.id} />}

              {/* __OPERATING_LIMITS__ no city runs / max radius from the home point (an existing truck) */}
              {editingTruck && <OperatingLimitsEditor truckId={editingTruck.id} truckLabel={editingTruck.truck_number} />}

              {/* __TELEMATICS__ ELD provider + vehicle (Azuga): positions / readings switches, what the ELD reports */}
              {editingTruck && <TelematicsEditor truckId={editingTruck.id} />}

              {/* Notes */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Any additional notes about this truck..."
                />
              </div>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { resetForm(); setShowForm(false); }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--surface-3)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: saving ? 'var(--surface-3)' : 'var(--accent-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {saving ? 'Saving...' : (editingTruck ? 'Update Truck' : 'Add Truck')}
              </button>
            </div>
          </form>
        ) : (
          /* Truck List */
          <>
            {/* Actions Bar */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <input
                type="text"
                placeholder="Search trucks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--accent-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
              >
                + Add Truck
              </button>
            </div>

            {/* Truck List */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                Loading trucks...
              </div>
            ) : filteredTrucks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                {searchTerm ? 'No trucks match your search' : 'No trucks yet. Add one to get started!'}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '0.75rem',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {filteredTrucks.map((truck) => (
                  <div
                    key={truck.id}
                    style={{
                      backgroundColor: 'var(--surface-2)',
                      padding: '1rem',
                      borderRadius: '5px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {truck.truck_number}
                        </span>
                        <span style={{
                          padding: '0.2rem 0.5rem',
                          borderRadius: '3px',
                          fontSize: '0.75rem',
                          backgroundColor: getStatusColor(truck.status),
                          color: 'var(--text-primary)'
                        }}>
                          {getStatusLabel(truck.status)}
                        </span>
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        {truck.make && truck.model && (
                          <span>{truck.year ? `${truck.year} ` : ''}{truck.make} {truck.model}</span>
                        )}
                        {truck.license_plate && (
                          <span style={{ marginLeft: truck.make ? '1rem' : 0 }}>
                            Plate: {truck.license_plate}
                          </span>
                        )}
                      </div>
                      {(() => {
                        // __EQUIP_COMPAT__ type, what the check will use, and what is still missing
                        const cap = unitCapacity({ truck, trailer: null, combo: null });
                        const miss = missingSpecs(truck, 'truck');
                        return (
                          <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>{typeInfo('truck', truck.truck_type).label}</span>
                            {truck.is_active === false && <span style={{ color: 'var(--color-danger)', marginLeft: '0.5rem' }}>RETIRED</span>}
                            {cap && cap.payloadLb != null && <span style={{ color: 'var(--text-muted)' }}> | payload {fmtLb(cap.payloadLb, units)}</span>}
                            {cap && cap.deckLengthFt != null && <span style={{ color: 'var(--text-muted)' }}> | deck {fmtFt(cap.deckLengthFt, units)} x {fmtFt(cap.deckWidthFt, units)}</span>}
                            {(truck.features || []).length > 0 && <span style={{ color: 'var(--text-muted)' }}> | {(truck.features || []).join(', ')}</span>}
                            {miss.length > 0 && <div style={{ color: 'var(--color-warning)' }}>Missing for the equipment check: {miss.join(', ')}</div>}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleEdit(truck)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'var(--accent-blue)',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Edit
                      </button>
                      {deleteConfirmId === truck.id ? (
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Sure?</span>
                          <button onClick={() => handleDelete(truck)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Yes</button>
                          <button onClick={() => setDeleteConfirmId(null)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>No</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirmId(truck.id)} style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}>Delete</button>
                      )}  
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            <div style={{
              marginTop: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid var(--surface-2)',
              color: 'var(--text-muted)',
              fontSize: '0.9rem',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>
                {filteredTrucks.length} truck{filteredTrucks.length !== 1 ? 's' : ''} 
                {searchTerm && ` matching "${searchTerm}"`}
              </span>
              <span>
                {trucks.filter(t => t.status === 'available').length} available | 
                {trucks.filter(t => t.status === 'in_use').length} in use | 
                {trucks.filter(t => t.status === 'maintenance').length} maintenance
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TruckManagementModal;
